import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { quotationsAPI } from '../../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { printStyles, CompanyLetterhead } from './shared';
import {
  ALL_COLUMNS, DEFAULT_PROFILES, MANDATORY_IDS,
  autoHideEmptyColumns, getFontConfig, getColumnWidths, getVisibleColumns, getItemValue
} from '../../config/tableColumns';
import { priceBasisLabel } from '../../components/quotations/constants';
import { computeLineItemTotals, formatQtyTotal, formatWeightTotal } from '../../utils/lineItemTotals';

export function QuotationPrintView() {
  const { id } = useParams();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const containerRef = React.useRef(null);

  // Check if opened in preview mode (inside modal iframe)
  const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await quotationsAPI.get(id);
        setQuotation(res.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load quotation');
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Auto-print when opened directly (not in preview mode)
  useEffect(() => {
    if (!loading && quotation && !printTriggered && !isPreviewMode) {
      setPrintTriggered(true);
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, quotation, printTriggered, isPreviewMode]);

  const formatCurrency = (amount, currency = 'INR') => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥', AED: 'AED ', SGD: 'S$' };
    return `${symbols[currency] || currency + ' '}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // 4 decimal places for unit price (Piece Rate)
  const formatCurrency4 = (amount, currency = 'INR') => {
    const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', JPY: '¥', AED: 'AED ', SGD: 'S$' };
    return `${symbols[currency] || currency + ' '}${(amount || 0).toFixed(4)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'white',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}>
        <style>{printStyles}</style>
        <div className="spinner" style={{ marginBottom: '20px' }}></div>
        <p style={{ color: '#1e40af', fontSize: '18px', fontWeight: '600' }}>
          Generating Quotation...
        </p>
      </div>
    );
  }

  if (error || !quotation) {
    return (
      <div className="print-container">
        <style>{printStyles}</style>
        <p style={{ color: 'red', textAlign: 'center' }}>{error || 'Quotation not found'}</p>
      </div>
    );
  }

  const currency = quotation.currency || 'INR';

  return (
    <>
      <div className="print-container landscape" ref={containerRef}>
        <style>{printStyles}</style>
        <style>{`
          @media print {
            @page { size: A4 landscape; margin: 8mm; }
            /* Pagination hygiene (page-break-inside / orphans / widows /
               repeating thead) lives in shared.js::printStyles so every
               print view inherits it. */
          }
        `}</style>
        
        {/* Print / Save as PDF Button - Hidden from print */}
        {!isPreviewMode && (
          <button 
            className="download-pdf-btn no-print" 
            onClick={() => window.print()}
            data-testid="quotation-preview-pdf-btn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Print / Save as PDF
          </button>
        )}
        
        {/* Professional Letterhead */}
        <CompanyLetterhead 
          documentType="QUOTATION"
          documentNumber={quotation.quotation_number}
          revisionNumber={quotation.revision_number}
        />

        {/* Customer & Quotation Info Grid */}
        <div className="info-grid">
          <div className="info-box">
            <h4>Customer Details</h4>
            <p><span className="value">{quotation.customer_name}</span></p>
            {quotation.customer_code && <p>Code: {quotation.customer_code}</p>}
            {quotation.customer_country && <p>Country: {quotation.customer_country}</p>}
            {quotation.customer_type && (
              <p style={{ color: quotation.customer_type === 'export' ? '#c2410c' : '#16a34a' }}>
                {quotation.customer_type === 'export' ? 'Export Customer' : 'Domestic Customer'}
              </p>
            )}
            {quotation.customer_contact_person && <p>Contact: {quotation.customer_contact_person}</p>}
          </div>
          <div className="info-box">
            <h4>Quotation Details</h4>
            <p>Date: <span className="value">{formatDate(quotation.created_at)}</span></p>
            <p>Valid Until: <span className="value">{quotation.validity_date ? formatDate(quotation.validity_date) : `${quotation.validity_days || 30} days`}</span></p>
            <p>Currency: <span className="value">{currency}</span></p>
            {quotation.delivery_terms && <p>Delivery Terms: <span className="value">{quotation.delivery_terms}</span></p>}
            {quotation.payment_terms && <p>Payment Terms: <span className="value">{quotation.payment_terms}</span></p>}
          </div>
        </div>

      {(quotation.inquiry_number || quotation.inquiry_date) && (
        <div style={{ marginBottom: '10px', fontSize: '12px', color: '#475569' }}>
          <strong>Ref:</strong> Inquiry {quotation.inquiry_number ? `#${quotation.inquiry_number}` : ''}
          {quotation.inquiry_date && ` dated ${formatDate(quotation.inquiry_date)}`}
        </div>
      )}

      {/* Items Table — Dynamic Columns */}
      {(() => {
        const items = quotation.items || [];
        let selectedIds = quotation.pdf_columns || DEFAULT_PROFILES.quotation;
        const hideEmpty = quotation.auto_hide_empty !== false;
        if (hideEmpty) selectedIds = autoHideEmptyColumns(items, selectedIds);
        const visibleCols = getVisibleColumns(selectedIds);
        const widths = getColumnWidths(selectedIds);
        const font = getFontConfig(visibleCols.length);

        const renderCellValue = (item, colId, idx) => {
          const currency = quotation.currency || 'INR';
          const netWeight = item.net_weight_grams || item.live_part?.net_weight_grams || 0;
          const qty = item.quantity || 0;
          const isKg = item.uom === 'kgs';
          const uom = isKg ? 'Kg' : 'Pcs';
          const description = item.part_description || item.description || '-';
          const customerPartNo = item.customer_part_no || item.live_part?.customer_part_no;
          // Part-No fallback: customer PN → internal PN → '-'. Never silently substitute the description.
          const displayPartNo = customerPartNo || item.part_internal_no || '-';

          switch (colId) {
            case 'row_num':      return idx + 1;
            case 'part_no':      return displayPartNo;
            case 'description':  return description;
            case 'hs_code':      return item.hs_code || item.live_part?.hs_code || '-';
            case 'net_weight':   return netWeight ? netWeight.toFixed(2) : '-';
            case 'total_weight': { const tw = isKg ? qty : (netWeight * qty) / 1000; return tw > 0 ? tw.toFixed(2) : '-'; }
            case 'qty':          return qty.toLocaleString('en-IN', { maximumFractionDigits: isKg ? 2 : 0 });
            case 'uom':          return uom;
            case 'tool_cost':    return (item.tooling_cost || 0) > 0 ? formatCurrency(item.tooling_cost, currency) : '-';
            case 'sample_cost':  return (item.sample_cost || 0) > 0 ? formatCurrency(item.sample_cost, currency) : '-';
            case 'sample_lt':    return item.sample_lead_time || '-';
            case 'mfg_lt':       return item.production_lead_time || item.manufacturing_lead_time || '-';
            case 'price':        return `${formatCurrency4(item.unit_price, currency)} / ${priceBasisLabel(item)}`;
            case 'total':        return formatCurrency(item.line_total, currency);
            default:             return '-';
          }
        };

        const cellStyle = (col) => {
          const base = {
            textAlign: col.align,
            verticalAlign: 'top',
            padding: font.padding,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          };
          if (col.id === 'part_no') return { ...base, fontWeight: '600', fontFamily: 'monospace', whiteSpace: 'normal' };
          if (col.id === 'description') return { ...base, whiteSpace: 'normal' };
          return base;
        };

        const totals = computeLineItemTotals(items);
        const renderTotalCell = (colId) => {
          switch (colId) {
            case 'description':   return `TOTAL (${totals.count} item${totals.count === 1 ? '' : 's'})`;
            case 'qty':           return formatQtyTotal(totals);
            case 'total_weight':  return formatWeightTotal(totals);
            case 'tool_cost':     return totals.toolingCost ? formatCurrency(totals.toolingCost, currency) : '-';
            case 'sample_cost':   return totals.sampleCost ? formatCurrency(totals.sampleCost, currency) : '-';
            case 'total':         return formatCurrency(totals.lineTotal, currency);
            default:              return '';
          }
        };

        return (
          <table className="items-table" style={{ fontSize: font.body, tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                {visibleCols.map(col => (
                  <th key={col.id} style={{ width: widths[col.id], textAlign: col.align, fontSize: font.header, padding: font.padding }}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.id || idx}>
                  {visibleCols.map(col => (
                    <td key={col.id} style={cellStyle(col)}>{renderCellValue(item, col.id, idx)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f1f5f9', fontWeight: 700 }}>
                {visibleCols.map(col => (
                  <td key={col.id} style={{ ...cellStyle(col), borderTop: '2px solid #1e40af' }}>
                    {renderTotalCell(col.id)}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        );
      })()}

      {/* Totals */}
      <div className="totals-section">
        <div className="totals-box">
          <div className="totals-row">
            <span>Subtotal:</span>
            <span>{formatCurrency(quotation.subtotal, currency)}</span>
          </div>
          {(quotation.tooling_total || quotation.tooling_cost || 0) > 0 && (
            <div className="totals-row">
              <span>Tooling Cost:</span>
              <span>{formatCurrency(quotation.tooling_total || quotation.tooling_cost, currency)}</span>
            </div>
          )}
          {(quotation.sample_total || quotation.sample_cost || 0) > 0 && (
            <div className="totals-row">
              <span>Sample Cost:</span>
              <span>{formatCurrency(quotation.sample_total || quotation.sample_cost, currency)}</span>
            </div>
          )}
          {(quotation.handling_charge || 0) > 0 && (
            <div className="totals-row">
              <span>Handling Charge:</span>
              <span>{formatCurrency(quotation.handling_charge, currency)}</span>
            </div>
          )}
          {(quotation.tax_amount || 0) > 0 && (
            <div className="totals-row">
              <span>Tax ({quotation.tax_percentage}%):</span>
              <span>{formatCurrency(quotation.tax_amount, currency)}</span>
            </div>
          )}
          {(quotation.discount_amount || 0) > 0 && (
            <div className="totals-row">
              <span>Discount ({quotation.discount_percentage}%):</span>
              <span>-{formatCurrency(quotation.discount_amount, currency)}</span>
            </div>
          )}
          <div className="totals-row total">
            <span>Grand Total:</span>
            <span>{formatCurrency(quotation.total_amount, currency)}</span>
          </div>
        </div>
      </div>

      {/* Delivery & Payment Terms */}
      <div style={{ marginTop: '15px', fontSize: '11px' }}>
        {quotation.delivery_terms && (
          <p><strong>Delivery Terms:</strong> {quotation.delivery_terms}</p>
        )}
        {quotation.payment_terms && (
          <p><strong>Payment Terms:</strong> {quotation.payment_terms}</p>
        )}
      </div>

      {/* Terms & Conditions Section */}
      {quotation.terms_conditions && (
        <div className="terms-section" style={{ marginTop: '20px' }}>
          <h4>Terms & Conditions</h4>
          {quotation.terms_conditions.split('\n').map((line, i) => (
            <p key={`line-${i}`}>{line}</p>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="footer">
        <p>For queries, contact us at info@singhaniainternational.com</p>
        <p>Generated on {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
      </div>
    </div>
    </>
  );
}

// Process Flow Print View - Confidential Layout (No Costs)

